import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Listbox,
  ListboxButton,
  ListboxOption,
  ListboxOptions,
  Transition,
} from "@headlessui/react";
import { CheckIcon, SelectorIcon } from "@heroicons/react/outline";
import dayjs from "dayjs";
import {
  requestAddMainBusiness,
  requestListAccountingTransactionImport,
  requestListBusiness,
  useAccountingDispatch,
  useAccountingState,
  useAuthDispatch,
  useAuthState,
  useBusinessTransactionDispatch,
  useBusinessTransactionState,
} from "../../Context";
import { classNames, getAccountingFileName } from "../../Utils";
import Badge from "../../Components/Badge";
import TableSkeleton from "../../Components/TableSkeleton";
import ErrorStateTable from "../../Components/ErrorStateTable";
import { Pagination } from "../../Components/Pagination";
import { Button } from "../../Components/Button";
import { isBusinessMainBlocked } from "../../Constants/business";

const AccountingTransactionImportDraftList = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);

  const businessIdParam = searchParams.get("business");

  const authDispatch = useAuthDispatch();
  const businessDispatch = useBusinessTransactionDispatch();
  const accountingDispatch = useAccountingDispatch();
  const { user } = useAuthState();
  const { businesses, loading: businessLoading } =
    useBusinessTransactionState();
  const { draftImports, draftImportsLoading, draftImportsError, nextTokens } =
    useAccountingState();

  const [selectedBusiness, setselectedBusiness] = useState({});
  const [page, setpage] = useState(1);

  useEffect(() => {
    if (!businesses.length && user.id) {
      requestListBusiness(businessDispatch, {
        owner: user.id,
        page: 1,
        limit: 10,
      });
    }
  }, [businessDispatch, businesses.length, user.id]);

  useEffect(() => {
    // Function to set selected business
    const setSelectedBusinessLogic = async () => {
      if (businessIdParam) {
        if (businesses.length > 0) {
          const business = businesses.find(
            (item) => item.value === businessIdParam
          );
          if (business) {
            setselectedBusiness({
              ...business,
              label: business.name,
              value: business.id,
            });
          } else {
            // Handle if business is not found, select index 0
            setselectedBusiness({
              ...businesses[0],
              label: businesses[0].name,
              value: businesses[0].id,
            });
          }
        } else {
          // Handle if businesses are empty, no business to select
        }
      } else if (user.mainBusiness) {
        // Set selectedBusiness directly from user.mainBusiness
        setselectedBusiness({
          ...user.mainBusiness,
          label: user.mainBusiness.name,
          value: user.mainBusiness.id,
        });
      } else if (businesses.length > 0) {
        // Set selected business to the first item in the list
        // Prefer a healthy business: a RESTRICTED / PENDING_DELETION one must
        // not become the account-wide main business. Falls back to the first
        // entry when every business is blocked.
        const mainCandidate =
          businesses.find((business) => !isBusinessMainBlocked(business)) ||
          businesses[0];
        setselectedBusiness({
          ...mainCandidate,
          label: mainCandidate.name,
          value: mainCandidate.id,
        });
        requestAddMainBusiness(authDispatch, {
          businessID: mainCandidate.id,
        });
      } else {
        // Handle if businesses are empty, no business to select
      }
    };

    setSelectedBusinessLogic();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [businesses, businessIdParam, user.mainBusiness]);

  useEffect(() => {
    if (selectedBusiness?.id) {
      requestListAccountingTransactionImport(accountingDispatch, {
        businessID: selectedBusiness.id,
        page: 1,
        limit: 10,
      });
    }
  }, [accountingDispatch, selectedBusiness?.id]);

  const onNext = () => {
    const nextPage = page + 1;
    setpage(nextPage);
    if (nextTokens[nextPage]) {
      requestListAccountingTransactionImport(accountingDispatch, {
        page: nextPage,
        limit: 10,
        businessID: selectedBusiness?.id,
        nextToken: nextTokens[nextPage],
      });
    }
  };

  const onPrev = () => {
    const prevPage = page - 1;
    setpage(prevPage);
    if (prevPage > 0) {
      requestListAccountingTransactionImport(accountingDispatch, {
        page: prevPage,
        limit: 10,
        businessID: selectedBusiness?.id,
        nextToken: nextTokens[prevPage],
      });
    }
  };

  const onPageChange = (selectedPage) => {
    setpage(selectedPage);
    if (nextTokens[selectedPage] || selectedPage === 1) {
      requestListAccountingTransactionImport(accountingDispatch, {
        page: selectedPage,
        limit: 10,
        businessID: selectedBusiness?.id,
        nextToken: nextTokens[selectedPage],
      });
    }
  };

  return (
    <div className="flex flex-col gap-6">
      <div className="flex sm:flex-row flex-col justify-between gap-4">
        <div className="flex sm:flex-row flex-col justify-between gap-4">
          <div>
            <Listbox value={selectedBusiness} onChange={setselectedBusiness}>
              {({ open }) => (
                <>
                  <ListboxButton
                    className={classNames(
                      businessLoading || businesses.length === 0
                        ? "bg-gray-200 border-transparent text-gray-500 cursor-not-allowed"
                        : "bg-white",
                      "flex items-center justify-between w-full sm:w-48 py-2 px-3 border border-gray-300 rounded-md shadow-sm text-sm"
                    )}
                  >
                    <span className="max-w-full ml-3 mr-2 text-sm text-ellipsis overflow-hidden sm:whitespace-nowrap">
                      {businesses.length === 0
                        ? t("No Business Profile Yet")
                        : selectedBusiness?.label || t("Select Business Name")}
                    </span>
                    {businessLoading ? (
                      <svg
                        role="status"
                        className="w-5 h-5 animate-spin text-white"
                        viewBox="0 0 100 101"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                        aria-hidden="true"
                      >
                        <path
                          d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
                          fill="#E5E7EB"
                        />
                        <path
                          d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
                          fill="currentColor"
                        />
                      </svg>
                    ) : (
                      <SelectorIcon className="w-5 h-5 text-gray-500" />
                    )}
                  </ListboxButton>
                  <Transition
                    show={open}
                    leave="transition ease-in duration-100"
                    leaveFrom="opacity-100"
                    leaveTo="opacity-0"
                  >
                    <ListboxOptions className="absolute z-10 mt-1 bg-white shadow-lg max-h-56 w-11/12 rounded-md py-1 ring-1 ring-black ring-opacity-5 overflow-auto focus:outline-none text-sm sm:w-auto">
                      {businesses.map((business) => (
                        <ListboxOption
                          key={business.value}
                          className={({ focus }) =>
                            classNames(
                              focus
                                ? "text-white bg-blue-600"
                                : "text-gray-900",
                              "cursor-default select-none relative py-2 pl-3 pr-9"
                            )
                          }
                          value={business}
                        >
                          {({ selected, focus }) => (
                            <>
                              <div className="flex items-center justify-between">
                                <span
                                  className={classNames(
                                    selected ? "font-semibold" : "font-normal",
                                    "ml-3 mr-2 block truncate"
                                  )}
                                >
                                  {business.label}
                                </span>
                                {selected ? (
                                  <span
                                    className={classNames(
                                      focus ? "text-white" : "text-blue-600",
                                      "absolute inset-y-0 right-0 flex items-center pr-4"
                                    )}
                                  >
                                    <CheckIcon className="h-5 w-5" />
                                  </span>
                                ) : null}
                              </div>
                            </>
                          )}
                        </ListboxOption>
                      ))}
                    </ListboxOptions>
                  </Transition>
                </>
              )}
            </Listbox>
          </div>
        </div>
        <div>
          <Button
            className="w-full md:w-auto"
            type="button"
            disabled={businessLoading}
            title={t("newImportTitle")}
            onClick={() =>
              navigate(
                `/accounting-transaction/create?business=${selectedBusiness?.id}`
              )
            }
          />
        </div>
      </div>
      <div className="flex flex-col">
        <div className="overflow-x-auto -mx-4 sm:-mx-6 lg:-mx-8">
          <div className="inline-block min-w-full align-middle md:px-6 lg:px-8">
            <div className="overflow-hidden border border-gray-200 rounded-lg shadow-sm">
              <table className="min-w-full divide-y divide-gray-300">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-6">
                      {t("File Name")}
                    </th>
                    <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                      {t("Status")}
                    </th>
                    <th
                      colSpan={2}
                      className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                    >
                      {t("Expired")}
                    </th>
                  </tr>
                </thead>
                {draftImportsLoading ? (
                  <TableSkeleton rows={3} columns={3} />
                ) : (
                  <tbody className="divide-y divide-gray-200 bg-white">
                    {draftImports?.length > 0 ? (
                      draftImports.map((item, index) => (
                        <tr key={index}>
                          <td className="whitespace-nowrap py-4 pl-4 pr-3 text-sm sm:pl-6">
                            <div className="flex flex-col items-start">
                              <div className="font-medium text-gray-900">
                                {getAccountingFileName(item.file?.key)}
                              </div>
                              <div className="text-gray-500">
                                {t("created")}{" "}
                                {dayjs(item.createdAt)
                                  .locale(i18n.language)
                                  .format("DD MMMM YYYY")}
                              </div>
                            </div>
                          </td>
                          <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                            {item.status === "PENDING" ? (
                              <Badge title="Pending" type="alert" />
                            ) : null}
                            {item.status === "SUCCESS" ? (
                              <Badge title="Success" type="success" />
                            ) : null}
                            {item.status === "FAILED" ? (
                              <Badge title="Failed" type="danger" />
                            ) : null}
                          </td>
                          <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                            {dayjs(item.expiredAt)
                              .locale(i18n.language)
                              .format("DD MMMM YYYY")}
                          </td>
                          <td className="relative whitespace-nowrap py-4 pl-3 pr-4 text-right text-sm font-medium text-blue-600 sm:pr-6">
                            <a
                              href={`/accounting-transaction/draft/${item.id}`}
                            >
                              {t("View")}
                            </a>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <ErrorStateTable
                        colSpan={3}
                        error={draftImportsError}
                        emptyTitle="No import data"
                      />
                    )}
                  </tbody>
                )}
              </table>
              <Pagination
                page={page}
                onPrev={onPrev}
                onNext={onNext}
                onPageChange={onPageChange}
                nextTokens={nextTokens}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AccountingTransactionImportDraftList;
